import type { DateValue } from "../model/date-value.type";
import { Field } from "../model/field.interface";
import { FieldOption } from "../model/field-option.interface";
import type { FieldType } from "../model/field-type.type";
import { toBooleanValue, toNumberValue, toDateValue, toTextValue } from "./field.function";

function generateGuid(): string {
    return crypto.randomUUID();
}

function toOptions(value: undefined | null | string[]): FieldOption[] {
    if (value === undefined || value === null)
        return [];
    return value.map(option => ({ label: option }));
}

export class FieldBuilder {

    public createField(field: {
        type: FieldType,
        label?: string,
        guid?: null |string,
    }): Field {
        return {
            label: field.label || '',
            guid: field.guid || generateGuid(),
            value: '',
            type: field.type,
            required: false,
            minValue: '',
            maxValue: '',
            options: [],
        };
    }

    public createBooleanField(field: {
        label?: string,
        guid?: null |string,
        value?: null | boolean,
    }): Field {
        return {
            label: field.label || '',
            guid: field.guid || generateGuid(),
            value: toBooleanValue(field.value),
            type: 'boolean',
            required: true,
            minValue: '',
            maxValue: '',
            options: [],
        };
    }

    public createNumberField(field: {
        label?: string,
        guid?: null | string,
        value?: null | number,
        required?: null | boolean,
        minValue?: null | number,
        maxValue?: null | number,
    }): Field {
        return {
            label: field.label || '',
            guid: field.guid || generateGuid(),
            value: toNumberValue(field.value),
            type: 'number',
            required: field.required || false,
            minValue: toNumberValue(field.minValue),
            maxValue: toNumberValue(field.maxValue),
            options: [],
        };
    }

    public createDateField(field: {
        label?: string,
        guid?: null | string,
        value?: null | DateValue,
        required?: null | boolean,
        minValue?: null | DateValue,
        maxValue?: null | DateValue,
    }): Field {
        return {
            label: field.label || '',
            guid: field.guid || generateGuid(),
            value: toDateValue(field.value),
            type: 'date',
            required: field.required || false,
            minValue: toDateValue(field.minValue),
            maxValue: toDateValue(field.maxValue),
            options: [],
        };
    }

    public createTextField(field: {
        label?: string,
        guid?: null | string,
        value?: null | string,
        required?: null | boolean,
        minLength?: null | number,
        maxLength?: null | number,
    }): Field {
        return {
            label: field.label || '',
            guid: field.guid || generateGuid(),
            value: toTextValue(field.value),
            type: 'text',
            required: field.required || false,
            minValue: toNumberValue(field.minLength),
            maxValue: toNumberValue(field.maxLength),
            options: [],
        };
    }

    public createLongTextField(field: {
        label?: string,
        guid?: null | string,
        value?: null | string,
        required?: null | boolean,
        minLength?: null | number,
        maxLength?: null | number,
    }): Field {
        return {
            label: field.label || '',
            guid: field.guid || generateGuid(),
            value: toTextValue(field.value || ''),
            type: 'long-text',
            required: field.required || false,
            minValue: toNumberValue(field.minLength),
            maxValue: toNumberValue(field.maxLength),
            options: [],
        };
    }

    public createListField(field: {
        label?: string,
        guid?: null | string,
        value?: null | string,
        required?: null | boolean,
        options?: null | string[],
    }): Field {
        return {
            label: field.label || '',
            guid: field.guid || generateGuid(),
            value: toTextValue(field.value || ''),
            type: 'list',
            required: field.required || false,
            minValue: '',
            maxValue: '',
            options: toOptions(field.options),
        };
    }

}
